// The SPA's end of the fan-out bus — `extension-iframe-utils.service.ng1.ts` plus the
// `UserAuthStateChange` arm of `extension-iframe-utils-listener.service.ng1.ts`.
//
// ⚠️ THE TRANSPORT EXISTED BEFORE THIS DID, AND THAT WAS THE BUG. `1bdfe12` built the whole
// page→content script→worker→page path and proved a message crosses two tabs — but nothing
// in the SPA ever published or subscribed, so signing in from the popup still left an open
// iframe showing the login form until it was reloaded by hand. A verified pipe with no
// callers is indistinguishable, from the outside, from no pipe at all.
import {
  BroadcastChannelService,
  BroadcastChannelTopic,
  IFrameBroadcastChannelMessageType,
  isUserAuthStateChange,
  UserAuthStateChangeOrigin,
  type BroadcastChannelManager,
  type IUserAuthStateChangeParams,
  type UserAuthStateChangeEvent,
} from '@woo-ext/protocol'

export type AuthStateChangeHandler = (params: IUserAuthStateChangeParams) => void

/**
 * ⚠️ ONE SERVICE AND ONE CHANNEL PER PAGE, SHARED BY THE PUBLISHER AND EVERY SUBSCRIBER.
 *
 * The service stamps each send with a per-page id and drops anything arriving under it,
 * which is the only thing standing between this and an echo: the worker fans out to EVERY
 * matching tab including the sender. Give the publisher its own service and the two ids
 * differ, the page hears its own sign-out, re-reads, re-publishes — and two surfaces ping
 * each other until one is closed. Upstream gets this free by injecting one singleton; here
 * it has to be deliberate.
 */
let channel: BroadcastChannelManager | undefined
const handlers = new Set<AuthStateChangeHandler>()

function authChannel(): BroadcastChannelManager {
  channel ??= new BroadcastChannelService().createAndSubscribe(
    BroadcastChannelTopic.IFrameChannel,
    (message) => {
      // Four other message types share this topic — `ReloadState`, `goToState` and the two
      // halves of the single-listener handshake — and they arrive here too.
      if (!isUserAuthStateChange(message)) return
      for (const handler of [...handlers]) handler(message.data)
    },
  )
  return channel
}

/**
 * `notifyUserAuthStateChange`: tell every other surface that the signed-in user changed.
 *
 * The caller does NOT also call its own handlers — it has just performed the navigation
 * itself, which is exactly what upstream's `notifyAuthStateChange` does before broadcasting.
 */
export function publishAuthStateChange(
  event: UserAuthStateChangeEvent,
  data?: Record<string, unknown>,
): void {
  const params: IUserAuthStateChangeParams = {
    event,
    // `Context`, not `Popup`: upstream's two origins name where the change HAPPENED, and
    // everything in this file runs in a page. The popup stamps its own.
    origin: UserAuthStateChangeOrigin.Context,
    ...(data === undefined ? {} : { data }),
  }
  authChannel().postMessage({
    type: IFrameBroadcastChannelMessageType.UserAuthStateChange,
    data: params,
  })
}

/** Subscribe to auth changes raised by any OTHER surface. Returns the unsubscribe. */
export function subscribeToAuthStateChange(handler: AuthStateChangeHandler): () => void {
  authChannel()
  handlers.add(handler)
  return () => {
    handlers.delete(handler)
  }
}

/** Test seam: drops the channel so the next call builds a fresh one. */
export function resetAuthBroadcastForTest(): void {
  channel?.destroy()
  channel = undefined
  handlers.clear()
}
